import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Brain, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { FileUploadZone } from '../components/BusinessPlan/FileUploadZone';
import { ObjectiveInputs } from '../components/BusinessPlan/ObjectiveInputs';
import { FinancialDashboard } from '../components/BusinessPlan/FinancialDashboard';
import { SimulationControls } from '../components/BusinessPlan/SimulationControls';
import { ScenarioComparison } from '../components/BusinessPlan/ScenarioComparison';
import { SectorBenchmark } from '../components/BusinessPlan/SectorBenchmark';
import { SimilarProjectsPanel } from '../components/BusinessPlan/SimilarProjectsPanel';
import { useBusinessPlanStore } from '../store/businessPlanStore';
import { useProjectStore } from '../store/projectStore';
import { useAuthStore } from '../store/authStore';
import { useESSReferenceStore } from '../store/essReferenceStore';
import type { BusinessPlanUpload, FinancialObjectives, SimulationParameters } from '../types/businessPlan';

export const BusinessPlanPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currentProject, fetchProject } = useProjectStore();
  const {
    currentPlan,
    scenarios,
    isLoading,
    isAnalyzing,
    isSimulating,
    error,
    uploadAndAnalyze,
    runSimulation,
    saveScenario,
    loadPlan,
    clearError
  } = useBusinessPlanStore();

  const {
    getBenchmarkByCategory,
    findSimilarProjects,
    compareWithSector,
    currentBenchmark,
    similarProjects,
    loadSectorBenchmarks
  } = useESSReferenceStore();

  const [objectives, setObjectives] = useState<FinancialObjectives>({
    ca_target: 500000,
    marge_target: 15,
    break_even_target: 12,
    investment_budget: 100000,
    growth_rate_target: 10
  });

  const [benchmarkComparisons, setBenchmarkComparisons] = useState<any[]>([]);
  const [currentStep, setCurrentStep] = useState<'upload' | 'analysis' | 'simulation'>('upload');

  useEffect(() => {
    if (projectId) {
      fetchProject(projectId);
      loadPlan(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    loadSectorBenchmarks();
  }, []);

  useEffect(() => {
    if (currentProject && currentProject.domain) {
      const category = getDomainCategory(currentProject.domain);
      if (category) {
        getBenchmarkByCategory(category);
        findSimilarProjects(category, objectives);
      }
    }
  }, [currentProject, objectives]);

  useEffect(() => {
    if (currentProject && currentProject.domain && currentBenchmark) {
      const category = getDomainCategory(currentProject.domain);
      if (category) {
        compareWithSector(category, objectives).then(comparisons => {
          setBenchmarkComparisons(comparisons);
        });
      }
    }
  }, [currentProject, objectives, currentBenchmark]);

  const getDomainCategory = (domain: string): string | null => {
    const mapping: Record<string, string> = {
      'ENVIRONNEMENT': 'Environnement / Économie circulaire',
      'SERVICES': 'Insertion / Services de proximité',
      'AGRICULTURE': 'Agroécologie / Circuits courts',
      'FORMATION': 'Formation / Innovation / Inclusion numérique',
      'ARTISANAT': 'Réemploi / Insertion'
    };
    return mapping[domain] || null;
  };

  useEffect(() => {
    if (currentPlan) {
      setCurrentStep('analysis');
      setObjectives(currentPlan.objectives);
    }
  }, [currentPlan]);

  const handleFileUpload = async (file: File) => {
    if (!projectId) return;
    
    const upload: BusinessPlanUpload = {
      file,
      objectives
    };

    try {
      await uploadAndAnalyze(projectId, upload);
      setCurrentStep('analysis');
    } catch (error) {
      console.error('Error uploading and analyzing:', error);
    }
  };

  const handleTextUpload = async (text: string) => {
    if (!projectId) return;
    
    const upload: BusinessPlanUpload = {
      raw_text: text,
      objectives
    };

    try {
      await uploadAndAnalyze(projectId, upload);
      setCurrentStep('analysis');
    } catch (error) {
      console.error('Error analyzing text:', error);
    }
  };

  const handleSimulation = async (parameters: SimulationParameters) => {
    if (!currentPlan) return;
    
    try {
      await runSimulation(currentPlan.id, parameters, `Simulation ${new Date().toLocaleString('fr-FR')}`);
      setCurrentStep('simulation');
    } catch (error) {
      console.error('Error running simulation:', error);
    }
  };

  const handleSaveScenario = async (name: string, parameters: SimulationParameters) => {
    if (!currentPlan) return;
    
    try {
      await runSimulation(currentPlan.id, parameters, name);
    } catch (error) {
      console.error('Error saving scenario:', error);
    }
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce scénario ?')) {
      try {
        // TODO: Implement delete scenario
        console.log('Delete scenario:', scenarioId);
      } catch (error) {
        console.error('Error deleting scenario:', error);
      }
    }
  };

  const handleViewScenario = (scenario: any) => {
    // TODO: Show scenario details in modal
    console.log('View scenario:', scenario);
  };

  const handleBack = () => {
    navigate(`/projects/${projectId}`);
  };

  const canAccessBusinessPlan = user?.profile_type === 'PORTEUR' || 
    (currentProject && (
      currentProject.owner_id === user?.id || 
      currentProject.assigned_advisor_id === user?.id ||
      ['COORDINATEUR', 'FINANCEUR', 'ADMIN'].includes(user?.profile_type || '')
    ));

  if (!canAccessBusinessPlan) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Accès Restreint</h2>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas les permissions pour accéder au business plan de ce projet.
            </p>
            <button
              onClick={() => navigate('/projects')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retour aux projets
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour au projet</span>
          </button>
          
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center space-x-3 mb-2">
              <Brain className="w-8 h-8" />
              <h1 className="text-3xl font-bold">Business Plan IA</h1>
            </div>
            <p className="text-blue-100 text-lg">
              {currentProject?.name || 'Analyse financière et simulation de scénarios'}
            </p>
            
            {/* Progress Steps */}
            <div className="mt-6 flex items-center space-x-4">
              {[
                { key: 'upload', label: 'Données', icon: '📁' },
                { key: 'analysis', label: 'Analyse', icon: '🧠' },
                { key: 'simulation', label: 'Simulation', icon: '🧪' }
              ].map((step, index) => (
                <div key={step.key} className="flex items-center space-x-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep === step.key 
                      ? 'bg-white text-blue-600' 
                      : index < ['upload', 'analysis', 'simulation'].indexOf(currentStep)
                        ? 'bg-blue-400 text-white'
                        : 'bg-blue-500/50 text-blue-200'
                  }`}>
                    {step.icon}
                  </div>
                  <span className="text-blue-100 text-sm">{step.label}</span>
                  {index < 2 && <div className="w-8 h-0.5 bg-blue-400" />}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <p className="text-red-600">{error}</p>
              </div>
              <button
                onClick={clearError}
                className="text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        {currentStep === 'upload' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Upload Zone */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  1. Téléversez vos Données Financières
                </h2>
                <FileUploadZone
                  onFileSelect={handleFileUpload}
                  onTextInput={handleTextUpload}
                  isLoading={isAnalyzing}
                />

                {isAnalyzing && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      <div>
                        <p className="font-medium text-blue-900">Analyse IA en cours...</p>
                        <p className="text-sm text-blue-700">
                          L'IA analyse vos données financières et génère les indicateurs clés
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Objectives */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  2. Définissez vos Objectifs
                </h2>
                <ObjectiveInputs
                  objectives={objectives}
                  onChange={setObjectives}
                  sectorBenchmark={currentBenchmark?.metrics}
                  projectCategory={currentProject?.domain ? getDomainCategory(currentProject.domain) || undefined : undefined}
                />
              </div>
            </div>

            {/* Benchmarks and Similar Projects */}
            {currentProject && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SectorBenchmark
                  comparisons={benchmarkComparisons}
                  category={currentProject.domain ? getDomainCategory(currentProject.domain) || currentProject.domain : 'Non défini'}
                />
                <SimilarProjectsPanel
                  similarProjects={similarProjects}
                />
              </div>
            )}
          </div>
        )}

        {currentStep === 'analysis' && currentPlan && (
          <div className="space-y-6">
            {/* Success Message */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-green-800 font-medium">
                  Analyse terminée ! Votre business plan a été analysé avec succès.
                </p>
              </div>
            </div>

            {/* Financial Dashboard */}
            <FinancialDashboard 
              analysis={currentPlan.analysis_result} 
              onKPIUpdate={(updatedKPIs) => {
                // Update the current plan with new KPIs
                const updatedPlan = {
                  ...currentPlan,
                  analysis_result: {
                    ...currentPlan.analysis_result,
                    kpis: updatedKPIs
                  }
                };
                // Update the store
                setCurrentPlan(updatedPlan);
              }}
            />

            {/* Simulation Controls */}
            <SimulationControls
              onSimulate={handleSimulation}
              onSaveScenario={handleSaveScenario}
              isSimulating={isSimulating}
              currentKPIs={currentPlan.analysis_result.kpis}
            />

            {/* Scenario Comparison */}
            {scenarios.length > 0 && (
              <ScenarioComparison
                basePlan={currentPlan}
                scenarios={scenarios}
                onDeleteScenario={handleDeleteScenario}
                onViewScenario={handleViewScenario}
              />
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8 flex items-center justify-center space-x-4">
          {currentStep === 'upload' && (
            <div className="text-center text-gray-500">
              <p className="text-sm">
                💡 Astuce : Plus vos données sont détaillées, plus l'analyse IA sera précise
              </p>
            </div>
          )}
          
          {currentStep === 'analysis' && (
            <button
              onClick={() => setCurrentStep('upload')}
              className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Nouvelle Analyse</span>
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
};